<template>
  <div
    :class="{
      'personalized-section block': true,
    }"
  >
    <template v-if="componentType === 'header'">
      <!-- render all Header Personas section builder in the dom for resolving Internal Link -->
      <div
        v-for="(variation, index) in variations"
        :key="index"
        :class="{
          hidden: variation.personalizationVariant !== currentPersonalization,
        }"
      >
        <component
          :is="getComponent(component._type)"
          v-for="component in variation.sectionBuilder?.sectionBuilder"
          :key="component._key"
          :value="component"
        />
      </div>
    </template>
    <template v-else>
      <div
        v-for="(variation, index) in variations"
        :key="index"
      >
        <!-- render the only correct Persona section builder for Latest News section -->
        <div
          v-if="variation.personalizationVariant === personalizedVariation.personalizationVariant"
        >
          <component
            :is="getComponent(component._type)"
            v-for="component in variation.sectionBuilder?.sectionBuilder"
            :key="component._key"
            :value="component"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { PersonalizedSectionBuilderRefQueryResult } from '~/sanity/types';
import { getComponent } from '~/components/Mapper';

const props = defineProps<{
  data: PersonalizedSectionBuilderRefQueryResult;
  componentType?: string;
}>();

if (!props.data?.personalizationDimension?.reference || !props.data?.variations) {
  throw new Error('personalizationDimension is required');
}

const { variations } = props.data;
const dimension = props.data.personalizationDimension?.reference;
const { productUsagePersona, isPaid, countryPersona, statePersona, regionPersona } =
  usePersonalization();
const { isLoggedIn } = useCrossSession();
const defaultCurrency = useState<string>('default-currency');

// default variant, should be included in all personalizations
const defaultVariationSectionBuilder = variations.find(
  (variation) => variation.personalizationVariant === 'default'
);

if (!defaultVariationSectionBuilder) {
  throw new Error('No Default Variant provided');
}

const currentPersonalization = ref('default');
const currencyPersonalization = ref(defaultCurrency.value);
watchEffect(
  () => {
    currentPersonalization.value = productUsagePersona.value.persona;
    currencyPersonalization.value = defaultCurrency.value;
  },
  {
    flush: 'post',
  }
);

const personalizedVariation = computed(() => {
  // for reactivity
  const personaVal = productUsagePersona.value.persona;
  const countryPersonaVal = countryPersona.value;
  const statePersonaVal = statePersona.value;
  const regionPersonaVal = regionPersona.value;
  const currencyVal = currencyPersonalization.value;
  const isPaidVal = isPaid.value;
  const loggedInStatus = isLoggedIn.value;

  if (!dimension || !dimension.dimensionName) {
    console.error('Invalid Personalization Dimension');
    return defaultVariationSectionBuilder;
  }

  switch (dimension.dimensionName) {
    // based on Product Usage
    case 'Product Usage': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return name === personaVal;
      });

      if (!variant) {
        return defaultVariationSectionBuilder;
      }

      return variant;
    }
    // based on whether or not the user is on a paid plan
    case 'Paid Plan': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return isPaidVal ? name === 'paid' : name === 'default';
      });

      if (!variant) {
        console.error('Invalid Paid Plan Variant');
        return defaultVariationSectionBuilder;
      }

      return variant;
    }
    // based on currency selection
    case 'Currency': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return name === currencyVal;
      });

      if (!variant) {
        return defaultVariationSectionBuilder;
      }

      return variant;
    }
    // based on SSO login status
    case 'SSO Status': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return name === loggedInStatus;
      });

      if (!variant) {
        return defaultVariationSectionBuilder;
      }

      return variant;
    }
    // based on heartbeat country
    case 'Country': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return name === countryPersonaVal;
      });
      if (!variant) {
        return defaultVariationSectionBuilder;
      }
      return variant;
    }
    // based on heartbeat business region
    case 'Region': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return name === regionPersonaVal;
      });
      if (!variant) {
        return defaultVariationSectionBuilder;
      }
      return variant;
    }
    // based on heartbeat US state
    case 'State': {
      const variant = variations.find((variation) => {
        const name = variation.personalizationVariant;
        return name === statePersonaVal;
      });
      if (!variant) {
        return defaultVariationSectionBuilder;
      }
      return variant;
    }
    case 'Landing Page':
    case 'City':
    case 'Language':
    case 'Industry':
    default: {
      return defaultVariationSectionBuilder;
    }
  }
});
</script>
<style>
.personalized-section .code-embed iframe {
  width: 100% !important;
}
</style>
